'use client';

import React, { useState, useEffect } from 'react';
import { useLogging } from 'src/context/logging';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useLocalStorage from 'src/hooks/system/useLocalStorage';
import { StrategyInfoDrawer } from 'src/components/Strategy/shared';
import { CircularProgress } from 'src/components';
import {
  StrategyCenterHeader,
  StrategyList,
  StrategyMap,
  StrategyCenterEmptyState,
} from './components';
import { useGetMyChainedStrategiesQuery } from 'src/api/redux';

const DEFAULT_STRATEGY_COLUMN_VISIBILITY = {
  crm_links: true,
  chain_ids: true,
  specialty: true,
  soc_type: true,
  city: true,
  affiliations: true,
  list_membership: true,
};

const StrategyCenter = () => {
  const log = useLogging();
  const { selectedChainIds, view, setView } = useStrategies(LOCATION.STRATEGY_CENTER);
  const [search, setSearch] = useState('');
  const [showAddToListDialog, setShowAddToListDialog] = useState(false);
  const [listCount, setListCount] = useState<{ total: number; isFetching: boolean }>({
    total: 0,
    isFetching: false,
  });
  const [savedColumnVisibility, setSavedColumnVisibility] = useLocalStorage(
    'strategyListColumns',
    DEFAULT_STRATEGY_COLUMN_VISIBILITY
  );
  const { isDrawerOpen, closeDrawer } = useDrawer();
  const drawerId = 'strategy-info';

  const {
    data: chainedStrategies,
    isLoading: isStrategiesLoading,
    isUninitialized: isStrategiesUninitialized,
  } = useGetMyChainedStrategiesQuery();

  // Determine if we're ready to render main content
  const strategiesReady =
    !isStrategiesLoading && !isStrategiesUninitialized;
  const hasStrategies = chainedStrategies?.results?.length > 0;
  const hasChainSelection = selectedChainIds.length > 0;

  const isReady =
    strategiesReady && (!hasStrategies || hasChainSelection);

  useEffect(() => {
    log.event('Strategy Center viewed', {
      source: 'StrategyCenter',
      view: 'initial',
      selectedChainIds,
    });
  }, [selectedChainIds, log]);

  useEffect(() => {
    log.event('Strategy Center viewed', {
      source: 'StrategyCenter',
      view,
    });
  }, [view]);

  const handleCloseDrawer = () => {
    closeDrawer(drawerId);
  };

  // Determine empty state reason (in priority order)
  const getEmptyStateReason = () => {
    if (strategiesReady && !hasStrategies) {
      return 'noStrategies';
    }
    if (strategiesReady && hasStrategies && !hasChainSelection) {
      return 'selectStrategy';
    }
    return null;
  };

  const emptyStateReason = getEmptyStateReason();
  const showContent = isReady && !emptyStateReason;
  const isLoading =
    isStrategiesLoading || isStrategiesUninitialized;

  return (
    <>
      <div
        data-testid="strategy-center"
        className="h-screen pb-1 w-full bg-level-1 flex flex-col overflow-hidden"
      >
        <StrategyCenterHeader
          view={view}
          setView={setView}
          search={search}
          onSearchChange={setSearch}
          onAddToList={() => setShowAddToListDialog(true)}
          savedColumnVisibility={savedColumnVisibility}
          onColumnVisibilityChange={setSavedColumnVisibility}
          listCount={listCount}
        />

        <div
          data-testid="strategy-center-body"
          className="flex-1 bg-white flex flex-col gap-4 min-h-0"
        >
          {showContent ? (
            <>
              {/* <StrategyCenterSubHeader /> */}

              <div
                data-testid="content-area"
                className="flex-1 bg-white rounded-lg shadow shadow-neutral-050 overflow-hidden min-h-0"
              >
                {view === 'map' ? (
                  <StrategyMap search={search} />
                ) : (
                  <StrategyList
                    search={search}
                    showAddToListDialog={showAddToListDialog}
                    onCloseAddToList={() => setShowAddToListDialog(false)}
                    savedColumnVisibility={savedColumnVisibility}
                    onCountChange={setListCount}
                  />
                )}
              </div>
            </>
          ) : isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <CircularProgress />
            </div>
          ) : (
            <StrategyCenterEmptyState
              reason={emptyStateReason || 'selectStrategy'}
            />
          )}
        </div>
      </div>
      <StrategyInfoDrawer
        isOpen={isDrawerOpen(drawerId)}
        onClose={handleCloseDrawer}
      />
    </>
  );
};

export default StrategyCenter;
